import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import RRHHNativeShell from './RRHHNativeShell';
import AdminTaskEditor, { ADMIN_TASK_STATUSES } from './AdminTaskEditor';
import { taskDeadlineLabel, taskDisplayDate } from './adminTaskDates';
import { rrhhCoreGet, rrhhCorePatch, rrhhCorePost } from './rrhhCoreApi';
import {
  addIsoDays,
  currentIsoDate,
  parseIsoDate,
  personDisplayName,
} from './rrhhUtils';
import './RRHHNative.css';

function dateKey(value) {
  return String(value || '').slice(0, 10);
}

function mondayFor(value) {
  const date = parseIsoDate(value) || new Date();
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function shortDayLabel(value) {
  const date = parseIsoDate(value);
  if (!date) return value;
  const weekday = date.toLocaleDateString('es-UY', { weekday: 'long' });
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)} ${String(date.getDate()).padStart(2, '0')}`;
}

function statusLabel(value) {
  return ADMIN_TASK_STATUSES.find((item) => item.value === value)?.label || 'Pendiente';
}

function cellKey(personId, day) {
  return `${personId}-${day}`;
}

export default function TareasAdminPage() {
  const { user } = useAuth();
  const canEdit = canWrite(user, 'rrhh.tareas');
  const [weekStart, setWeekStart] = useState(mondayFor(currentIsoDate()));
  const [people, setPeople] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [drafts, setDrafts] = useState({});
  const [draggedId, setDraggedId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [historical, setHistorical] = useState(false);
  const [weekBounds, setWeekBounds] = useState({ min: -4, max: 4 });
  const requestRef = useRef(0);
  const dragEndedAt = useRef(0);
  const currentMonday = mondayFor(currentIsoDate());
  const weekOptions = useMemo(() => Array.from({ length: weekBounds.max - weekBounds.min + 1 }, (_, index) => {
    const offset = weekBounds.min + index;
    const value = addIsoDays(currentMonday, offset * 7);
    const from = parseIsoDate(value);
    const to = parseIsoDate(addIsoDays(value, 6));
    const label = `${from.getDate()} ${from.toLocaleDateString('es-UY', { month: 'short' })} - ${to.getDate()} ${to.toLocaleDateString('es-UY', { month: 'short' })}`;
    return { value, offset, label: offset === 0 ? `Semana actual · ${label}` : label };
  }), [currentMonday, weekBounds]);

  function selectWeek(value) {
    setWeekStart(value);
    setEditor(null);
    if (!historical) return;
    const offset = weekOptions.find((option) => option.value === value)?.offset;
    setWeekBounds((current) => ({
      min: offset === current.min ? current.min - 52 : current.min,
      max: offset === current.max ? current.max + 52 : current.max,
    }));
  }

  function toggleHistory() {
    if (historical) {
      setWeekBounds({ min: -4, max: 4 });
      setWeekStart(currentMonday);
    } else {
      setWeekBounds({ min: -52, max: 52 });
    }
    setHistorical(!historical);
    setEditor(null);
  }

  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addIsoDays(weekStart, index)), [weekStart]);
  const tasksByCell = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      const key = cellKey(task.personnel_id, dateKey(task.due_date));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(task);
    });
    return map;
  }, [tasks]);

  async function refresh(silent = false) {
    const request = ++requestRef.current;
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await rrhhCoreGet(`/tareas-admin?week_start=${encodeURIComponent(weekStart)}`);
      if (request !== requestRef.current) return;
      setPeople(data.people || []);
      setTasks(data.tasks || []);
    } catch (err) {
      if (request === requestRef.current) setError(err.message || 'No se pudieron cargar las tareas.');
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [weekStart]);

  useEffect(() => {
    if (editor || savingKey || draggedId) return undefined;
    const refreshVisible = () => { if (document.visibilityState === 'visible') refresh(true); };
    const interval = window.setInterval(refreshVisible, 60000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [weekStart, editor, savingKey, draggedId]);

  function updateDraft(key, value) {
    setDrafts((current) => ({ ...current, [key]: value }));
  }

  async function createTask(person, day) {
    const key = cellKey(person.id, day);
    const title = String(drafts[key] || '').trim();
    if (!title || !canEdit || savingKey) return;
    ++requestRef.current;
    setSavingKey(key); setError(''); setNotice('');
    try {
      const created = await rrhhCorePost('/tareas-admin', {
        personnel_id: person.id,
        due_date: day,
        target_date: day,
        title,
        status: 'pendiente',
      });
      setTasks((current) => [...current, created]);
      setDrafts((current) => ({ ...current, [key]: '' }));
      setNotice('Tarea creada');
    } catch (err) {
      setError(err.message || 'No se pudo crear la tarea.');
    } finally {
      setSavingKey('');
    }
  }

  async function updateTask(task, patch = {}) {
    if (!canEdit) throw new Error('Sin permiso para editar tareas.');
    ++requestRef.current;
    setSavingKey(`task-${task.id}`); setError(''); setNotice('');
    try {
      const updated = await rrhhCorePatch(`/tareas-admin/${task.id}`, patch);
      setTasks((current) => current.map((item) => Number(item.id) === Number(task.id) ? updated : item));
    } finally {
      setSavingKey('');
    }
  }

  async function archiveTask(task) {
    if (!canEdit) throw new Error('Sin permiso para archivar tareas.');
    ++requestRef.current;
    setSavingKey(`task-${task.id}`); setError(''); setNotice('');
    try {
      await rrhhCorePost(`/tareas-admin/${task.id}/archive`, {});
      setTasks((current) => current.filter((item) => Number(item.id) !== Number(task.id)));
    } finally {
      setSavingKey('');
    }
  }

  function onDropTask(person, day) {
    const task = tasks.find((item) => Number(item.id) === Number(draggedId));
    setDraggedId(null);
    if (!task || !canEdit || savingKey) return;
    if (Number(task.personnel_id) === Number(person.id) && dateKey(task.due_date) === day) return;
    updateTask(task, { personnel_id: person.id, due_date: day }).catch((err) => setError(err.message || 'No se pudo mover la tarea.'));
  }

  return <RRHHNativeShell title="Tareas" className="rrhh-admin-tasks-page">
    {error && <div className="alert alert-error">{error}</div>}
    {notice && <div className="alert alert-success">{notice}</div>}

    <section className="rrhh-admin-tasks-toolbar">
      <div className="rrhh-plan-control rrhh-admin-tasks-week">
        <label htmlFor="admin-tasks-week">Semana</label>
        <div className="rrhh-plan-week-picker-row">
          <select id="admin-tasks-week" className="form-input" value={weekStart} disabled={Boolean(savingKey)} onChange={(event) => selectWeek(event.target.value)}>
            {weekOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button className={`btn btn-ghost rrhh-plan-history-button${historical ? ' active' : ''}`} type="button" aria-pressed={historical} disabled={Boolean(savingKey)} onClick={toggleHistory}>Histórico</button>
        </div>
      </div>
      <div className="rrhh-admin-task-legend" aria-label="Estados de las tareas">
        {ADMIN_TASK_STATUSES.map((option) => <span className={`status-${option.value}`} key={option.value}>{option.label}</span>)}
      </div>
    </section>

    <section className="rrhh-native-card rrhh-admin-tasks-wrap">
      {loading ? <div className="rrhh-native-empty">Cargando tareas...</div> : <table className="rrhh-admin-tasks-board">
        <thead>
          <tr>
            <th>Persona</th>
            {days.map((day) => <th key={day}>{shortDayLabel(day)}</th>)}
          </tr>
        </thead>
        <tbody>
          {people.map((person) => <tr key={person.id}>
            <th className="rrhh-admin-tasks-person">
              <strong>{personDisplayName(person)}</strong>
              <span>{person.operational_role || 'Administración'}</span>
            </th>
            {days.map((day) => {
              const key = cellKey(person.id, day);
              const cellTasks = tasksByCell.get(key) || [];
              return <td
                key={key}
                className="rrhh-admin-task-cell"
                onDragOver={(event) => canEdit && event.preventDefault()}
                onDrop={(event) => { event.preventDefault(); onDropTask(person, day); }}
              >
                <div className="rrhh-admin-task-list">
                  {cellTasks.map((task) => <button
                    type="button"
                    key={task.id}
                    className={`rrhh-admin-task-pill status-${task.status || 'pendiente'}${Number(draggedId) === Number(task.id) ? ' dragging' : ''}`}
                    draggable={canEdit && !savingKey}
                    aria-label={`${task.title}: ${statusLabel(task.status)}, ${taskDeadlineLabel(task)}`}
                    title={`${task.title} · ${statusLabel(task.status)}${task.notes ? '\n' + task.notes : ''}`}
                    aria-haspopup="dialog"
                    disabled={Boolean(savingKey)}
                    onClick={(event) => { if (Date.now() - dragEndedAt.current > 250) setEditor({ task, anchor: event.currentTarget }); }}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      if (!canEdit || savingKey) return;
                      setEditor({ task, anchor: event.currentTarget, mode: 'status', point: event.clientX || event.clientY ? { x: event.clientX, y: event.clientY } : undefined });
                    }}
                    onDragStart={(event) => { event.dataTransfer.setData('text/plain', String(task.id)); event.dataTransfer.effectAllowed = 'move'; setDraggedId(task.id); }}
                    onDragEnd={() => { dragEndedAt.current = Date.now(); setDraggedId(null); }}
                  >
                    <span>{task.title}</span>
                    <small className="rrhh-admin-task-target">Objetivo: {taskDisplayDate(task.target_date)}</small>
                    <small className={`rrhh-admin-task-deadline${task.overdue_days > 0 ? ' overdue' : ''}`}>{taskDeadlineLabel(task)}</small>
                  </button>)}
                  {canEdit && <form className="rrhh-admin-task-create" onSubmit={(event) => { event.preventDefault(); createTask(person, day); }}>
                    <input
                      value={drafts[key] || ''}
                      onChange={(event) => updateDraft(key, event.target.value)}
                      placeholder="+ tarea"
                      aria-label={`Nueva tarea para ${personDisplayName(person)}, ${shortDayLabel(day)}`}
                      disabled={Boolean(savingKey)}
                    />
                  </form>}
                </div>
              </td>;
            })}
          </tr>)}
          {!people.length && <tr><td colSpan={8} className="rrhh-native-empty">No hay personas activas con rol operativo Administración.</td></tr>}
        </tbody>
      </table>}
    </section>
    {editor && <AdminTaskEditor key={editor.task.id} {...editor} canEdit={canEdit} onSave={updateTask} onArchive={archiveTask} onClose={() => setEditor(null)} />}
  </RRHHNativeShell>;
}
